// ChannelPlanAlignmentDrilldown.jsx
import React, { useMemo, useState, useCallback } from "react";
import { LicenseManager } from "ag-charts-enterprise";
import "ag-charts-community";
import "ag-charts-enterprise";
import { AgCharts } from "ag-charts-react";
import { OmniTooltipNextComponent, OmniIconComponent, LoaderComponent , Utils} from "../../../../../shared";

import { AGGRID_LICENCE_KEY } from "../../../../../shared";
LicenseManager.setLicenseKey(AGGRID_LICENCE_KEY);

const asNumber = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};


// Mock data with multiple channels


// Helper to build a lookup map from orderedData by channel name
const buildOrderedDataMap = (orderedData, isLocal = false) => {
    const orderDataChannel = orderedData?.parent_campaign_details?.channels
    const map = new Map();
    if (!orderDataChannel || !Array.isArray(orderDataChannel)) return map;
    
    orderDataChannel.forEach((item) => {
        const channelName = item.channel_name || item.channel || item.name || 'Unknown Channel';
        
        // Get ordered_total (Prisma) - use local value if isLocal is true
        const orderedPrisma = asNumber(
            isLocal 
                ? (item.ordered_total_local || item.ordered_total || 0)
                : (item.ordered_total || 0)
        );
        
        // Get ordered_total_moh (MOH) - use local value if isLocal is true
        const orderedMoh = asNumber(
            isLocal 
                ? (item.ordered_total_local_moh || item.ordered_total_moh || 0)
                : (item.ordered_total_moh || 0)
        );
        
        // Build nested map for publishers/suppliers
        const children = item.publishers || item.suppliers || item.nestedData || [];
        const childrenMap = new Map();
        children.forEach((child) => {
            const childName = child.supplier || child.publisher_name || child.name || 'Unknown';
            
            // Get child ordered_total (Prisma)
            const childOrderedPrisma = asNumber(
                isLocal 
                    ? (child.ordered_total_local || child.ordered_total || 0)
                    : (child.ordered_total || 0)
            );
            
            // Get child ordered_total_moh (MOH)
            const childOrderedMoh = asNumber(
                isLocal 
                    ? (child.ordered_total_local_moh || child.ordered_total_moh || 0)
                    : (child.ordered_total_moh || 0)
            );
            
            childrenMap.set(childName, { 
                orderedPrisma: childOrderedPrisma, 
                orderedMoh: childOrderedMoh 
            });
        });
        
        map.set(channelName, { 
            orderedPrisma, 
            orderedMoh, 
            children: childrenMap 
        });
    });
    
    return map;
};

// Transform channels data to chart format with expand/collapse support
const buildChartData = (channelsData, expandedChannels, orderedData, isLocal = false, currencySymbol = 'USD') => {
    if (!channelsData || !Array.isArray(channelsData) || channelsData.length === 0) return [];
    
    const result = [];
    
    // Build lookup map from orderedData for ordered bars
    const orderedMap = buildOrderedDataMap(orderedData, isLocal);
    
    // Track channel name occurrences for duplicates
    const channelNameCount = {};
    
    channelsData.forEach((channelData, channelIndex) => {
        const baseChannelName = channelData.channel_name || channelData.channel || 'Unknown Channel';
        
        // Track duplicates and create unique display name
        channelNameCount[baseChannelName] = (channelNameCount[baseChannelName] || 0) + 1;
        const occurrence = channelNameCount[baseChannelName];
        const channelName = occurrence > 1 ? `${baseChannelName} (${occurrence})` : baseChannelName;
        const channelKey = `${baseChannelName}_${channelIndex}`;
        
        // Support multiple property names for children (publishers, suppliers, nestedData)
        const children = channelData.publishers || channelData.suppliers || channelData.nestedData || [];
        const supplierChildren = children.filter(c => c.publisher_type === 'Supplier' || !c.publisher_type);
        const hasChildren = supplierChildren && supplierChildren.length > 0;
        const isExpanded = expandedChannels.has(channelKey);
        
        // Get planned value from channelsData (grey bar) - use local value if isLocal is true
        const planned = asNumber(
            isLocal
                ? (channelData.channel_total_budget_local || channelData.planned_total_local || 
                   channelData.totalBudget_local || channelData.budget_local || channelData.planned_local ||
                   channelData.channel_total_budget || channelData.planned_total || 
                   channelData.totalBudget || channelData.budget || channelData.planned || 0)
                : (channelData.channel_total_budget || channelData.planned_total || 
                   channelData.totalBudget || channelData.budget || channelData.planned || 0)
        );
        
        // Get ordered values from orderedData map
        const orderedInfo = orderedMap.get(baseChannelName);
        const orderedPrisma = orderedInfo ? orderedInfo.orderedPrisma : 0;
        const orderedMoh = orderedInfo ? orderedInfo.orderedMoh : 0;
        
        // Add the parent channel row
        result.push({
            name: channelName,
            channelKey: channelKey,
            baseChannelName: baseChannelName,
            planned,
            orderedPrisma,
            orderedMoh,
            plannedFormatted: Utils.getLocalCurrency(currencySymbol, planned),
            orderedPrismaFormatted: Utils.getLocalCurrency(currencySymbol, orderedPrisma),
            orderedMohFormatted: Utils.getLocalCurrency(currencySymbol, orderedMoh),
            hasChildren,
            isExpanded,
            isChild: false,
            channelIndex,
        });
        
        // If expanded, add children
        if (isExpanded && hasChildren) {

            // Merge supplierChildren (from channelsData) with orderedInfo?.children (from orderedData)
            const plannedChildrenMap = new Map();
            supplierChildren.forEach((sup) => {
                const supName = sup.publisher_name || sup.supplier || sup.name || 'Unknown';
                plannedChildrenMap.set(supName, sup);
            });
            
            // Get all unique child names from both sources
            const orderedChildrenMap = orderedInfo?.children || new Map();
            const allChildNames = new Set([
                ...plannedChildrenMap.keys(),
                ...orderedChildrenMap.keys()
            ]);
            
            // Iterate over merged children
            allChildNames.forEach((supName) => {
                const sup = plannedChildrenMap.get(supName) || {};
                
                // Get planned value from channelsData children (grey bar) - use local value if isLocal is true
                const supPlanned = asNumber(
                    isLocal
                        ? (sup.publisher_budget_local || sup.channel_total_budget_local || 
                           sup.planned_total_local || sup.totalBudget_local || sup.budget_local || sup.planned_local ||
                           sup.publisher_budget || sup.channel_total_budget || 
                           sup.planned_total || sup.totalBudget || sup.budget || sup.planned || 0)
                        : (sup.publisher_budget || sup.channel_total_budget || 
                           sup.planned_total || sup.totalBudget || sup.budget || sup.planned || 0)
                );
                
                // Get ordered values from orderedData children map
                const childOrderedInfo = orderedChildrenMap.get(supName) || { orderedPrisma: 0, orderedMoh: 0 };
                const supOrderedPrisma = childOrderedInfo.orderedPrisma || 0;
                const supOrderedMoh = childOrderedInfo.orderedMoh || 0;
                
                result.push({
                    name: `  ${supName}`,
                    channelKey: channelKey,
                    baseChannelName: baseChannelName,
                    planned: supPlanned,
                    orderedPrisma: supOrderedPrisma,
                    orderedMoh: supOrderedMoh,
                    plannedFormatted: Utils.getLocalCurrency(currencySymbol, supPlanned),
                    orderedPrismaFormatted: Utils.getLocalCurrency(currencySymbol, supOrderedPrisma),
                    orderedMohFormatted: Utils.getLocalCurrency(currencySymbol, supOrderedMoh),
                    hasChildren: false,
                    isChild: true,
                    parentChannel: channelKey,
                });
            });
        }
    });
    
    return result;
};

const ChannelPlanAlignmentDrilldown = ({ channelsData, orderedData=null, islocalCurrency }) => {
    // Debug: Log incoming data to understand structure
    let isLoading = true;
    if (Array.isArray(orderedData) && orderedData.length === 0) {
        // Empty array - still loading
        isLoading = true;
    } else if (orderedData && typeof orderedData === 'object' && Object.keys(orderedData).length > 0) {
        // Object with values - done loading
        isLoading = false;
    } else if (orderedData == null) {
        // null or undefined - done loading
        isLoading = false;
    }
    
    // Determine if local currency should be used
    const isLocal = islocalCurrency?.islocal || false;
    const currencySymbol = islocalCurrency?.currencyName || 'USD';
    
    // Use mock data if channelsData is empty or invalid
    const validChannelsData = useMemo(() => {
        return channelsData;
    }, [channelsData]);

    const [expandedChannels, setExpandedChannels] = useState(new Set());

    const toggleChannel = useCallback((channelKey) => {
        setExpandedChannels((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(channelKey)) {
                newSet.delete(channelKey);
            } else {
                newSet.add(channelKey);
            }
            return newSet;
        });
    }, []);

    const chartData = useMemo(() => {
        return buildChartData(validChannelsData, expandedChannels, orderedData, isLocal, currencySymbol);
    }, [validChannelsData, expandedChannels, orderedData, isLocal, currencySymbol]);
    
    const renderer  = (datum) => {
        const value = datum.datum[datum.yKey+'Formatted'];
        return {
            data: [
                {
                    label: datum.yName,
                    value: value,
                },
            ],
        };
    }
    const options = useMemo(() => {
        return {
            legend: { enabled: false },
            series: [
                {
                    type: "bar",
                    xKey: "name",
                    yKey: "planned",
                    yName: "Planned (PlanIt)",
                    grouped: true,
                    direction: 'horizontal',
                    fill: "#D1D5DB",
                    stroke: "#D1D5DB",
                    cornerRadius: 4,
                    label: { enabled: false },
                    tooltip: {
                        renderer
                    }
                },
                {
                    type: "bar",
                    xKey: "name",
                    yKey: "orderedMoh",
                    yName: "Ordered (MOH)",
                    grouped: true,
                    direction: 'horizontal',
                    fill: "#96F5E6",
                    stroke: "#96F5E6",
                    cornerRadius: 4,
                    label: { enabled: false },
                    tooltip: {renderer}
                },
                {
                    type: "bar",
                    xKey: "name",
                    yKey: "orderedPrisma",
                    yName: "Ordered (Prisma)",
                    grouped: true,
                    direction: 'horizontal',
                    fill: "#2DD4BF",
                    stroke: "#2DD4BF",
                    cornerRadius: 4,
                    label: { enabled: false },
                    tooltip: {
                        renderer
                    }
                },
            ],
            data: chartData,
            
            padding: { top: 0, right: 24, bottom: 0, left: 0 },
            axes: [
                {
                    type: "category",
                    position: "left",
                    line: { enabled: false },
                    tick: { enabled: false },
                    label: { enabled: false },
                    gridLine: { style: [{ stroke: 'transparent' }] },
                },
                {
                    type: "number",
                    position: "bottom",
                    line: { enabled: false },
                    label: { enabled: false },
                    gridLine: { style: [{ stroke: '#E5E7EB', lineDash: [4, 4] }] },
                },
            ],
        };
    }, [chartData, currencySymbol]);

    // Calculate max value for x-axis (include all three bars)
    const maxValue = useMemo(() => {
        const allValues = chartData.flatMap(d => [d.planned || 0, d.orderedPrisma || 0, d.orderedMoh || 0]);
        return Math.max(...allValues, 0);
    }, [chartData]);

    // Format axis label
    const formatAxisValue = (val) => {
        if (val >= 1000000) return `${(val / 1000000).toFixed(0)}M`;
        if (val >= 1000) return `${(val / 1000).toFixed(0)}K`;
        return val.toString();
    };

    // Generate x-axis ticks
    const xAxisTicks = useMemo(() => {
        const tickCount = 5;
        const step = maxValue / (tickCount - 1);
        return Array.from({ length: tickCount }, (_, i) => Math.round(i * step));
    }, [maxValue]);

    const rowHeight = 55;
    const chartHeight = Math.max(200, chartData.length * rowHeight);

    // Find channel info for rendering labels
    const getChannelInfo = (channelKey, channelIndex) => {
        const channel = validChannelsData[channelIndex];
        if (!channel) return { hasChildren: false, isExpanded: false };
        const children = channel?.publishers || channel?.suppliers || channel?.nestedData || [];
        const supplierChildren = children.filter(c => c.publisher_type === 'Supplier' || !c.publisher_type);
        return {
            hasChildren: supplierChildren && supplierChildren.length > 0,
            isExpanded: expandedChannels.has(channelKey)
        };
    };

    return (
        isLoading ? (
            <div className="channel-plan-alignment-container chart-loader" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                <LoaderComponent spinnerCount={1} />
            </div>
        ) :
        <div className="channel-plan-alignment-container">
            {/* Legend at the top */}
            <div className="channel-plan-legend">
                <div className="legend-item">
                    <span className="legend-color" style={{ background: '#D1D5DB' }}></span>
                    <span className="legend-label">Planned (PlanIt)</span>
                    <OmniTooltipNextComponent style={{ width: "fit-content" }}>
                        <span slot="invoker" className="legend-info">
                            <OmniIconComponent className="is-size-3" icon-id="omni:informative:info" />
                        </span>
                        <div slot="content">The planned media spend as it appears in PlanIt. This amount will dynamically update if any changes are made in Planit.</div>
                    </OmniTooltipNextComponent>
                </div>
                <div className="legend-item">
                    <span className="legend-color" style={{ background: '#96F5E6' }}></span>
                    <span className="legend-label">Ordered (MOH)</span>
                    <OmniTooltipNextComponent style={{ width: "fit-content" }}>
                        <span slot="invoker" className="legend-info">
                            <OmniIconComponent className="is-size-3" icon-id="omni:informative:info" />
                        </span>
                        <div slot="content">Total amount across all placements shown in Media Ops Hub, including those not synced to Prisma.</div>
                    </OmniTooltipNextComponent>
                </div>
                <div className="legend-item">
                    <span className="legend-color" style={{ background: '#2DD4BF' }}></span>
                    <span className="legend-label">Ordered (Prisma)</span>
                    <OmniTooltipNextComponent style={{ width: "fit-content" }}>
                        <span slot="invoker" className="legend-info">
                            <OmniIconComponent className="is-size-3" icon-id="omni:informative:info" />
                        </span>
                        <div slot="content">Total amount of all ordered placements in Prisma. (Note: This number will be 0 if your orders aren’t integrated with Prisma.)</div>
                    </OmniTooltipNextComponent>
                </div>
            </div>

            {/* Scrollable chart area */}
            <div className="channel-plan-scroll-container">
                <div className="channel-plan-chart-wrapper" style={{ height: `${chartHeight}px` }}>
                    <div className="channel-labels">
                        {chartData.map((item, index) => {
                            const info = !item.isChild ? getChannelInfo(item.channelKey, item.channelIndex) : null;
                            return (
                                <div 
                                    key={`${item.channelKey}-${index}`}
                                    className={`channel-label-row ${item.isChild ? 'child-row' : 'parent-row'} ${info?.hasChildren ? 'clickable' : ''}`}
                                    onClick={() => !item.isChild && info?.hasChildren && toggleChannel(item.channelKey)}
                                >
                                    <span className="label-text">
                                        {item.orderedMoh > item.planned && (
                                            <>
                                                <OmniTooltipNextComponent style={{ width: "fit-content" }}>
                                                    <span slot="invoker" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                            <path d="M11.8854 10.4713L6.88709 0.558258C6.80237 0.389883 6.67517 0.248879 6.51925 0.150488C6.36332 0.0520964 6.1846 5.90925e-05 6.0024 0C5.8202 5.90925e-05 5.64148 0.0520964 5.48556 0.150488C5.32963 0.248879 5.20243 0.389883 5.11771 0.558258L0.119452 10.4713C0.0362291 10.6326 -0.00481491 10.814 0.000449631 10.9972C0.00571417 11.1804 0.0571016 11.3589 0.149442 11.5147C0.240527 11.6649 0.366848 11.7883 0.516426 11.8732C0.666005 11.9581 0.833884 12.0017 1.00414 11.9999H11.0007C11.1727 11.9999 11.3419 11.9535 11.4917 11.8652C11.6415 11.7769 11.767 11.6498 11.856 11.496C11.945 11.3423 11.9944 11.1672 11.9996 10.9877C12.0047 10.8081 11.9654 10.6303 11.8854 10.4713ZM5.50258 4.17389C5.50258 4.03552 5.55524 3.90282 5.64897 3.80497C5.74271 3.70713 5.86984 3.65216 6.0024 3.65216C6.13496 3.65216 6.2621 3.70713 6.35583 3.80497C6.44957 3.90282 6.50223 4.03552 6.50223 4.17389V7.30432C6.50223 7.44269 6.44957 7.57539 6.35583 7.67324C6.2621 7.77108 6.13496 7.82605 6.0024 7.82605C5.86984 7.82605 5.74271 7.77108 5.64897 7.67324C5.55524 7.57539 5.50258 7.44269 5.50258 7.30432V4.17389ZM6.02739 10.1739C5.83053 10.1727 5.64169 10.0923 5.50017 9.94944C5.35866 9.80658 5.27537 9.61225 5.26766 9.40691C5.26432 9.30378 5.28081 9.20098 5.31618 9.10461C5.35154 9.00824 5.40505 8.92025 5.47355 8.84583C5.54204 8.77142 5.62413 8.71209 5.71497 8.67135C5.8058 8.63061 5.90354 8.6093 6.0024 8.60866C6.19927 8.60984 6.38811 8.69023 6.52962 8.83309C6.67114 8.97595 6.75442 9.17027 6.76214 9.37561C6.76548 9.47875 6.74898 9.58154 6.71362 9.67791C6.67825 9.77428 6.62474 9.86228 6.55625 9.93669C6.48775 10.0111 6.40566 10.0704 6.31483 10.1112C6.22399 10.1519 6.12626 10.1732 6.02739 10.1739Z" fill="#FED882"/>
                                                        </svg>
                                                        {/* <OmniIconComponent className="is-size-4" icon-id="omni:informative:alertDark" style={{ color: '#f0ad4e' }} /> */}
                                                    </span>
                                                    <div slot="content">Your ordered amount is higher than your planned amount. Please check with your team to make sure your orders align with the approved budget.</div>
                                                </OmniTooltipNextComponent>
                                            </>
                                        )}
                                        {item.isChild ? item.name.trim() : item.name}
                                    </span>
                                    {!item.isChild && info?.hasChildren && (
                                        <span className="expand-icon">{info?.isExpanded ? '∨' : '>'}</span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    <div className="channel-chart" style={{ height: `${chartHeight}px` }}>
                        <AgCharts options={options} style={{ width: '100%', height: `${chartHeight}px` }} />
                    </div>
                </div>
            </div>

            {/* Sticky X-Axis at bottom */}
            <div className="channel-plan-xaxis">
                <div className="xaxis-spacer"></div>
                <div className="xaxis-labels">
                    {xAxisTicks.map((tick, index) => (
                        <span key={index} className="xaxis-tick">{formatAxisValue(tick)}</span>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default ChannelPlanAlignmentDrilldown;
